import Phaser from 'phaser';
import { App } from './App';
import { COLORS, FONTS, SLACK_WINDOW, TASKBAR, toHexColor } from '../config/gameConfig';
import { COPY } from '../content/copy';
import { formatFictionalTime } from '../systems/Clock';
import type { BadgePayload, EventBus, SlackMessage } from '../systems/EventBus';
import { autoMark } from '../ui/autoMark';
import { renderMarkedText } from '../ui/MarkedText';
import type { Tooltip } from '../ui/Tooltip';
import { Scrollable } from '../ui/Scrollable';

export class SlackApp extends App {
  private messages: SlackMessage[] = [];
  private unreadSinceFocus = 0;
  private jamieUnreadSinceOpen = 0;
  private pulseActive = false;
  private pulseTimer: Phaser.Time.TimerEvent | null = null;
  private listContainer!: Phaser.GameObjects.Container;
  private hitOverlay!: Phaser.GameObjects.Rectangle;
  private hitOverlayHitArea!: Phaser.Geom.Rectangle;
  private scrollable!: Scrollable;

  private readonly onSlackMessage = (p: { message: SlackMessage }) => {
    this.messages = [...this.messages, p.message];
    this.unreadSinceFocus += 1;
    this.rerender();
    this.bus.emit('slack:unread-changed', { count: this.unreadSinceFocus });
    if (p.message.author === 'jamie') {
      this.jamieUnreadSinceOpen += 1;
      this.triggerPulse();
    }
  };

  constructor(scene: Phaser.Scene, bus: EventBus, private readonly tooltip?: Tooltip) {
    super(scene, bus, {
      x: SLACK_WINDOW.x,
      y: SLACK_WINDOW.y,
      width: SLACK_WINDOW.width,
      height: SLACK_WINDOW.height,
      title: COPY.slack.title,
      windowId: 'slack',
      titleBarHeight: SLACK_WINDOW.titleBarHeight,
    });
    this.renderBody();
    this.bus.on('slack:message-received', this.onSlackMessage);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());

    this.bus.emit('slack:unread-changed', { count: this.unreadSinceFocus });
    this.emitBadge();
  }

  getBadge(): BadgePayload {
    return {
      label: 'Slack',
      count: this.jamieUnreadSinceOpen,
      pulse: this.pulseActive,
      iconColor: 0x4a154b,
    };
  }

  private emitBadge(): void {
    this.bus.emit('window:badge-changed', { windowId: this.options.windowId, badge: this.getBadge() });
  }

  private triggerPulse(): void {
    this.pulseActive = true;
    this.emitBadge();
    if (this.pulseTimer) this.pulseTimer.remove();
    this.pulseTimer = this.scene.time.delayedCall(TASKBAR.pulseHoldAfterEventMs, () => {
      this.pulseActive = false;
      this.pulseTimer = null;
      this.emitBadge();
    });
  }

  protected onRestored(): void {
    this.jamieUnreadSinceOpen = 0;
    if (this.pulseTimer) {
      this.pulseTimer.remove();
      this.pulseTimer = null;
    }
    this.pulseActive = false;
    this.emitBadge();
  }

  protected onResize(): void {
    this.hitOverlay.setSize(this.bodyWidth, this.bodyHeight);
    this.hitOverlayHitArea.width = this.bodyWidth;
    this.hitOverlayHitArea.height = this.bodyHeight;
    this.scrollable?.resize(this.bodyWidth, this.bodyHeight);
    this.rerender();
  }

  protected renderBody(): void {
    // Hit overlay first so it sits BEHIND the message list. Marked-term
    // hits inside list rows render later and win Phaser's input sort —
    // their definitions stay clickable. The overlay still catches clicks
    // on empty body area to clear unread counts. No hand cursor: clicking
    // empty body isn't a "clickable" action so the cursor must stay
    // default.
    this.hitOverlay = this.scene.add.rectangle(0, 0, this.bodyWidth, this.bodyHeight, 0x000000, 0)
      .setOrigin(0, 0);
    this.hitOverlayHitArea = new Phaser.Geom.Rectangle(0, 0, this.bodyWidth, this.bodyHeight);
    this.hitOverlay.setInteractive({
      hitArea: this.hitOverlayHitArea,
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
    });
    this.bodyContainer.add(this.hitOverlay);

    this.listContainer = this.scene.add.container(0, 0);
    this.bodyContainer.add(this.listContainer);
    this.scrollable = new Scrollable(this.scene, this, this.listContainer, this.bodyContainer);

    this.hitOverlay.on('pointerdown', () => {
      if (this.unreadSinceFocus === 0 && this.jamieUnreadSinceOpen === 0) return;
      if (this.unreadSinceFocus !== 0) {
        this.unreadSinceFocus = 0;
        this.bus.emit('slack:unread-changed', { count: 0 });
      }
      if (this.jamieUnreadSinceOpen !== 0) {
        this.jamieUnreadSinceOpen = 0;
        this.emitBadge();
      }
    });

    this.rerender();
  }

  private rerender(): void {
    if (!this.listContainer) return;
    this.listContainer.removeAll(true);

    if (this.messages.length === 0) {
      const empty = this.scene.add.text(
        SLACK_WINDOW.rowPaddingX,
        SLACK_WINDOW.rowPaddingY,
        COPY.slack.emptyHint,
        {
          fontFamily: FONTS.ui,
          fontSize: SLACK_WINDOW.bodyFontSize,
          color: toHexColor(COLORS.textMuted),
        },
      );
      this.listContainer.add(empty);
      this.scrollable?.setContentHeight(SLACK_WINDOW.rowPaddingY + empty.height + 8);
      return;
    }

    let y = SLACK_WINDOW.rowPaddingY;
    const textWidth = this.bodyWidth - SLACK_WINDOW.rowPaddingX * 2;
    for (const m of this.messages) {
      const header = this.scene.add.text(
        SLACK_WINDOW.rowPaddingX,
        y,
        `${authorLabel(m.author)} · ${formatFictionalTime(m.fictionalMinutes)}`,
        {
          fontFamily: FONTS.ui,
          fontSize: SLACK_WINDOW.authorFontSize,
          color: toHexColor(COLORS.textMuted),
        },
      );
      this.listContainer.add(header);
      y += header.height + 2;

      if (this.tooltip) {
        const segs = autoMark(m.body);
        const result = renderMarkedText(
          this.scene,
          this.bus,
          this.tooltip,
          this.listContainer,
          SLACK_WINDOW.rowPaddingX,
          y,
          segs,
          {
            fontSize: SLACK_WINDOW.bodyFontSize,
            color: toHexColor(COLORS.textPrimary),
          },
          { wrapWidth: textWidth },
        );
        y += result.height + SLACK_WINDOW.rowPaddingY + 4;
      } else {
        const body = this.scene.add.text(SLACK_WINDOW.rowPaddingX, y, m.body, {
          fontFamily: FONTS.ui,
          fontSize: SLACK_WINDOW.bodyFontSize,
          color: toHexColor(COLORS.textPrimary),
          wordWrap: { width: textWidth, useAdvancedWrap: true },
        });
        this.listContainer.add(body);
        y += body.height + SLACK_WINDOW.rowPaddingY + 4;
      }
    }
    this.scrollable?.setContentHeight(y + 8);
    this.refreshChildDepths();
  }

  private teardown(): void {
    if (this.pulseTimer) {
      this.pulseTimer.remove();
      this.pulseTimer = null;
    }
    this.bus.off('slack:message-received', this.onSlackMessage);
    this.scrollable?.destroy();
  }
}

function authorLabel(author: SlackMessage['author']): string {
  if (author === 'jamie') return 'Jamie';
  if (author === 'cfo') return 'Priya C. (CFO)';
  return 'Marcus (Billing)';
}
